import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import {
  IFavoriteBrandRow,
  IFavoriteCategoryRow,
  IFavoriteProductRow,
  ILocation,
} from 'src/app/features/customers/models/customers.models';
import { LanguageService } from 'src/app/config/language.service';

import {
  OrdersDisplayKpi,
  ProductFavoriteBrandRow,
  ProductFavoriteCategoryRow,
  ProductFavoriteProductRow,
  RecentOrdersGroup,
} from './recap-location.types';

/** Pure helpers shared by recap-location tab components. */
@Injectable({ providedIn: 'root' })
export class RecapHelpersService {
  constructor(
    private readonly translateService: TranslateService,
    private readonly languageService: LanguageService,
  ) {}

  toNumber(value: number | string | null | undefined): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  toDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  getNewestDate(first: Date | null, second: Date | null): Date | null {
    if (!first) return second;
    if (!second) return first;
    return first.getTime() >= second.getTime() ? first : second;
  }

  // ---------------------------------------------------------------------------
  // Favorite row normalization
  // ---------------------------------------------------------------------------

  normalizeFavoriteProduct(product: IFavoriteProductRow): ProductFavoriteProductRow {
    return {
      image: product.image || null,
      product_name: product.product_name || '',
      product_code: product.product_code || '',
      total_units: this.toNumber(product.total_units),
      total_orders: this.toNumber(product.total_orders),
      total_revenue: this.toNumber(product.total_revenue),
      last_ordered: this.toDate(product.last_ordered),
    };
  }

  normalizeFavoriteBrand(brand: IFavoriteBrandRow): ProductFavoriteBrandRow {
    return {
      brand_name: brand.brand_name || '',
      total_units: this.toNumber(brand.total_units),
      total_orders: this.toNumber(brand.total_orders),
      total_revenue: this.toNumber(brand.total_revenue),
    };
  }

  normalizeFavoriteCategory(category: IFavoriteCategoryRow): ProductFavoriteCategoryRow {
    return {
      category_name: category.category_name || '',
      total_units: this.toNumber(category.total_units),
      total_orders: this.toNumber(category.total_orders),
      total_revenue: this.toNumber(category.total_revenue),
    };
  }

  // ---------------------------------------------------------------------------
  // Monthly / peak / chart helpers
  // ---------------------------------------------------------------------------

  /** Normalizes monthly orders from array or dict API shapes. */
  normalizeMonthlyEntries(
    raw: any,
  ): { month: string; count: number; total_amount: number }[] {
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw
        .map((e: any) => ({
          month: e?.month ?? e?.year_month ?? '',
          count: Number(e?.count) || 0,
          total_amount: Number(e?.total_amount ?? e?.value) || 0,
        }))
        .filter(e => !!e.month);
    }
    return Object.entries(raw as Record<string, any>).map(([month, val]: [string, any]) => ({
      month,
      count: Number(val?.count) || 0,
      total_amount: Number(val?.total_amount ?? val) || 0,
    }));
  }

  normalizePeak(peak: any): { month: string; count: number } | null {
    if (!peak) return null;
    if (typeof peak === 'string') return { month: peak, count: 0 };
    if (typeof peak === 'object' && peak.month) {
      return { month: String(peak.month), count: Number(peak.count) || 0 };
    }
    return null;
  }

  formatPeakPeriod(
    peak: { month: string; count: number } | null,
    locale?: string,
  ): string | null {
    if (!peak?.month) return null;
    const [yearStr, monthStr] = peak.month.split('-');
    const monthIdx = parseInt(monthStr, 10) - 1;
    const year = parseInt(yearStr, 10);
    if (isNaN(monthIdx) || isNaN(year)) return peak.month;
    return new Date(year, monthIdx, 1).toLocaleString(
      locale || this.languageService.getCurrentLanguage() || 'en-US',
      { month: 'long', year: 'numeric' },
    );
  }

  formatYearMonth(yearMonth: string): string {
    const [yearStr, monthStr] = yearMonth.split('-');
    const monthIdx = parseInt(monthStr, 10) - 1;
    if (isNaN(monthIdx)) return yearMonth;
    return new Date(parseInt(yearStr, 10), monthIdx, 1).toLocaleString('en-US', { month: 'short' });
  }

  buildChartData(
    raw: { month: string; count: number; total_amount: number }[] | null | undefined,
  ): any {
    if (!raw || !raw.length) return null;
    const currentYear = new Date().getFullYear();
    const entries = raw
      .filter(e => !!e?.month && e.month.startsWith(String(currentYear)))
      .sort((a, b) => a.month.localeCompare(b.month));
    if (!entries.length) return null;
    return {
      labels: entries.map(e => this.formatYearMonth(e.month)),
      datasets: [
        {
          label: 'Orders Value',
          data: entries.map(e => e.total_amount),
          backgroundColor: '#74D3FF',
          borderColor: '#004768',
          borderWidth: 1,
        },
      ],
    };
  }

  buildChartOptions(): any {
    return {
      responsive: true,
      plugins: {
        legend: {
          display: false,
          position: 'top',
        },
      },
      scales: {
        x: { stacked: true },
        y: { stacked: true, min: 0 },
      },
    };
  }

  /** YoY revenue trend (%); null when both years are empty. */
  computeYearOverYearTrend(monthly: { month: string; total_amount: number }[]): number | null {
    const currentYear = new Date().getFullYear();
    const prevYear = currentYear - 1;
    if (!monthly?.length) return null;
    const currTotal = monthly
      .filter(e => e?.month?.startsWith(String(currentYear)))
      .reduce((s, e) => s + (Number(e.total_amount) || 0), 0);
    const prevTotal = monthly
      .filter(e => e?.month?.startsWith(String(prevYear)))
      .reduce((s, e) => s + (Number(e.total_amount) || 0), 0);
    if (prevTotal === 0 && currTotal === 0) return null;
    if (prevTotal === 0) return 100;
    return Math.round(((currTotal - prevTotal) / prevTotal) * 1000) / 10;
  }

  // ---------------------------------------------------------------------------
  // Aggregation across locations
  // ---------------------------------------------------------------------------

  /** Orders placed in a given calendar year, reconstructed from the all-time monthly buckets. */
  private countOrdersInYear(
    monthly: { month: string; count: number }[],
    year: number,
  ): number {
    // The dash matters: `${year}` alone would also match a malformed '20261'.
    const prefix = `${year}-`;
    return monthly
      .filter(e => e?.month?.startsWith(prefix))
      .reduce((sum, e) => sum + (Number(e.count) || 0), 0);
  }

  /**
   * Order-count-weighted mean of per-location averages: total units / total orders.
   * Null when the scoped year carries no orders at all.
   */
  private weightedAvgItemsPerOrder(
    samples: { avg: number | null; orders: number }[],
  ): number | null {
    const orders = samples.reduce((sum, s) => sum + s.orders, 0);
    if (orders <= 0) return null;
    const units = samples.reduce((sum, s) => sum + (Number(s.avg) || 0) * s.orders, 0);
    return Math.round((units / orders) * 100) / 100;
  }

  /** YoY delta in percent; null when the previous value is 0/missing. Mirrors the backend. */
  private computeAvgItemsDeltaPct(
    current: number | null,
    previous: number | null,
  ): number | null {
    if (current == null || previous == null || previous === 0) return null;
    return Math.round(((current - previous) / previous) * 10000) / 100;
  }

  aggregateLocationsKpi(locations: ILocation[]): OrdersDisplayKpi {
    const currentYear = new Date().getFullYear();
    const prevYear = currentYear - 1;

    let totalSales = 0;
    let totalOrders = 0;
    const currentSamples: { avg: number | null; orders: number }[] = [];
    const prevSamples: { avg: number | null; orders: number }[] = [];

    for (const loc of locations) {
      const kpi = loc.kpi;
      if (!kpi) continue;
      totalSales += Number(kpi.total_sales) || 0;
      totalOrders += Number(kpi.total_orders) || 0;

      // The per-location averages are year-scoped; `total_orders` is all-time, so the
      // weight has to be rebuilt from the monthly buckets (same recordset, per backend).
      const locMonthly = this.normalizeMonthlyEntries(kpi.total_orders_per_month);
      currentSamples.push({
        avg: kpi.avg_items_per_order ?? null,
        orders: this.countOrdersInYear(locMonthly, currentYear),
      });
      prevSamples.push({
        avg: kpi.avg_items_per_order_prev_year ?? null,
        orders: this.countOrdersInYear(locMonthly, prevYear),
      });
    }

    const monthly = this.aggregateMonthlyData(locations);
    let peak: { month: string; count: number } | null = null;
    for (const m of monthly) {
      if (!peak || m.count > peak.count) peak = { month: m.month, count: m.count };
    }

    const avgItems = this.weightedAvgItemsPerOrder(currentSamples);
    const avgItemsPrev = this.weightedAvgItemsPerOrder(prevSamples);

    return {
      total_sales: totalSales,
      total_orders: totalOrders,
      total_orders_per_month: monthly,
      peak_ordering_period: peak,
      peak_ordering_period_prev_year: null,
      peak_ordering_period_delta_pct: null,
      avg_items_per_order: avgItems,
      avg_items_per_order_prev_year: avgItemsPrev,
      avg_items_per_order_delta_pct: this.computeAvgItemsDeltaPct(avgItems, avgItemsPrev),
    };
  }

  aggregateMonthlyData(locations: ILocation[]): { month: string; count: number; total_amount: number }[] {
    const acc = new globalThis.Map<string, { count: number; total_amount: number }>();
    for (const loc of locations) {
      for (const entry of this.normalizeMonthlyEntries(loc.kpi?.total_orders_per_month)) {
        const cur = acc.get(entry.month) || { count: 0, total_amount: 0 };
        cur.count += entry.count;
        cur.total_amount += entry.total_amount;
        acc.set(entry.month, cur);
      }
    }
    return Array.from(acc.entries())
      .map(([month, v]) => ({ month, count: v.count, total_amount: v.total_amount }))
      .sort((a, b) => a.month.localeCompare(b.month));
  }

  mergeLocationsRecentOrders(locations: ILocation[]): any[] {
    const all: any[] = [];
    for (const loc of locations) {
      for (const order of loc.recent_orders || []) {
        all.push({
          ...order,
          location: order.location ?? (loc?.id != null ? { id: loc.id, name: loc.name } : undefined),
          location_id: order.location_id ?? loc?.id,
          location_name: order.location_name ?? loc?.name,
        });
      }
    }
    return all.sort((a, b) => {
      const ta = a?.date_order ? new Date(a.date_order).getTime() : 0;
      const tb = b?.date_order ? new Date(b.date_order).getTime() : 0;
      return tb - ta;
    });
  }

  // ---------------------------------------------------------------------------
  // Recent orders grouping
  // ---------------------------------------------------------------------------

  groupRecentOrders(orders: any[]): RecentOrdersGroup[] {
    if (!orders?.length) return [];
    const visible = orders.filter((order) => this.getRecentOrderStatus(order) !== 'deleted');
    if (!visible.length) return [];
    const sorted = [...visible].sort((a, b) => {
      const ta = a.date_order ? new Date(a.date_order).getTime() : 0;
      const tb = b.date_order ? new Date(b.date_order).getTime() : 0;
      return tb - ta;
    });
    const groups = new globalThis.Map<string, any[]>();
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    for (const order of sorted) {
      if (!order.date_order) continue;
      const d = new Date(order.date_order);
      const month = d.getMonth();
      const year = d.getFullYear();
      let label: string;
      if (month === currentMonth && year === currentYear) {
        label = this.translateService.instant('companies.orders_tab.this_month');
      } else {
        label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      }
      if (!groups.has(label)) groups.set(label, []);
      groups.get(label)!.push(order);
    }

    return Array.from(groups.entries()).map(([label, items]) => ({
      label,
      collapsed: false,
      items,
    }));
  }

  // ---------------------------------------------------------------------------
  // Status / delta presentation helpers
  // ---------------------------------------------------------------------------

  getStatusStyles(status: string): { [key: string]: string } {
    switch (status) {
      case 'sent_to_erp':
      case 'sale':
      case 'done':
        return { 'background-color': '#ADE9D1', 'color': '#006F43', 'border': '1px solid #77DBB3' };
      case 'pending_inventory':
        return { 'background-color': '#FFF2D0', 'color': '#765700', 'border': '1px solid #FFEAB0' };
      case 'cancel':
      case 'declined':
        return { 'background-color': '#FDD7D0', 'color': '#821500', 'border': '1px solid #FBBCB0' };
      case 'draft':
        return { 'background-color': '#E2E2E2', 'color': '#1C2B36', 'border': '1px solid transparent' };
      default:
        return { 'background-color': '#D5F2FF', 'color': '#004768', 'border': '1px solid #74D3FF' };
    }
  }

  getRecentOrderStatus(order: any): string {
    return order?.state || order?.status || order?.payment_status || '';
  }

  getStatusTranslation(status: string): string {
    if (!status) return '--';
    return this.translateService.instant(`quotations_and_orders.QuotationStatus.${status}`) || status;
  }

  statDeltaClass(delta: number | null): string {
    if (delta == null || delta === 0) return 'ord-trend-neutral';
    return delta > 0 ? 'ord-trend-positive' : 'ord-trend-negative';
  }

  statDeltaArrow(delta: number | null): string {
    if (delta == null || delta === 0) return '';
    return delta > 0 ? 'up' : 'down';
  }

  formatStatDeltaPct(delta: number | null): string {
    if (delta == null) return '--';
    return `${Math.abs(Math.round(delta))}%`;
  }
}
